import type { ApiProblemDetails } from "Infrastructure/Api/generated/types.gen";
import { translateApiError } from "Infrastructure/Api/translateApiError";
import { toast } from "sonner";

/**
 * Named HTTP status codes used across the app. Prefer these to magic numbers.
 */
export const HttpStatus = {
	Ok: 200,
	Created: 201,
	NoContent: 204,
	BadRequest: 400,
	Unauthorized: 401,
	Forbidden: 403,
	NotFound: 404,
	Conflict: 409,
	TooManyRequests: 429,
	InternalServerError: 500,
} as const;

export const isSuccessStatus = (status: number): boolean =>
	status >= 200 && status < 300;

export const isErrorStatus = (status: number): boolean =>
	status === 0 || status >= 400;

/**
 * True for a thrown {@link ApiCallError} that carries a 401 — the auth response
 * interceptor has already dispatched the forced-sign-out event, so further
 * handlers should skip.
 */
export const isUnauthorizedError = (error: unknown): boolean =>
	error instanceof ApiCallError && error.status === HttpStatus.Unauthorized;

/**
 * Per-mutation/per-query meta keys recognised by the global error handler.
 * Set `meta: { showError: false }` on a mutation/query that handles errors inline
 * (e.g. via `useHandleServerError`) — the global toast is then suppressed.
 */
export type ApiQueryMeta = {
	/** When false, suppresses the global error toast. Default: true. */
	showError?: boolean;
};

export class ApiCallError extends Error {
	readonly status: number;
	readonly body: ApiProblemDetails | null;
	readonly url: string;

	constructor(status: number, body: ApiProblemDetails | null, url: string) {
		super(`API ${status} at ${url}`);
		this.name = "ApiCallError";
		this.status = status;
		this.body = body;
		this.url = url;
	}

	protected isApiException = true;

	static isApiException(obj: unknown): obj is ApiCallError {
		return (obj as { isApiException?: boolean })?.isApiException === true;
	}
}

/**
 * Global error handler for TanStack Query mutations and queries.
 *
 * Behaviour:
 *   - 401 is delegated to the auth response interceptor (forced sign-out) — no toast.
 *   - `meta.showError === false` opts the call out of the global toast (inline form handling).
 *   - Otherwise toasts a Czech-translated message via `translateApiError`.
 */
export const handleResponseError = (
	error: unknown,
	meta?: ApiQueryMeta | null,
) => {
	if (isUnauthorizedError(error)) {
		return;
	}
	if (meta?.showError === false) {
		return;
	}
	const message = translateApiError(error);
	if (message) {
		toast.error(message);
	}
};
